import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import type { ScreenerReqSorting } from '@revelio/data-access';

import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import type { PrimaryFilterId } from '../../filters/types/filters';
import { buildScreenerFilters } from '../../filters/utils/get-screener-filters';
import { useChart } from '../Chart/ChartProvider';
import { useScreenerPageSync } from '../Chart/Table/useScreenerPageSync';
import { useScreenerSortingSync } from '../Chart/Table/useScreenerSortingSync';
import { filterEffectiveMetrics } from '../ChartConfig/utils/metric-isolation';
import {
  isScreenerMetric,
  type MetricValues,
  type ScreenerMetricValues,
} from '../PageConfigProvider/types/metric';
import { ScreenerTableDataProvider } from './ScreenerDataProvider';
import { type DataProviderChildren, DataProviderQueryStatus } from './types';
import type { ScreenerTableRow } from './utils/transform-screener-table-data';

export type ScreenerTableData = {
  rows: ScreenerTableRow[];
  totalCount: number;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  sorting: ScreenerReqSorting | undefined;
  setSorting: (sorting: ScreenerReqSorting | undefined) => void;
};

const LIMIT = 35;
const isValidPageParam = (value: string | null): boolean => {
  if (!value) return false;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0;
};

const getSegmentKeyFromSort = (
  sortBy: ScreenerReqSorting['sort_by'] | undefined
) => {
  if (!sortBy || !sortBy.startsWith('SORT_BY_')) {
    return undefined;
  }
  return sortBy.slice('SORT_BY_'.length).toLowerCase();
};

export const ScreenerTableMultiMetricProvider = ({
  primaryDimension,
  children,
}: {
  primaryDimension?: PrimaryFilterId;
  children: DataProviderChildren<ScreenerTableData>;
}) => {
  const { item } = useChart();
  const effectiveFilters = useEffectiveFilters();
  const [searchParams] = useSearchParams();
  const pageParam = searchParams.get('page');

  const defaultMetricOptions = item?.plot?.defaultMetricOptions || [];
  const filtered = filterEffectiveMetrics(defaultMetricOptions);
  const effectiveMetrics: MetricValues[] = filtered.length
    ? filtered
    : ['headcount'];

  const screenerMetrics = effectiveMetrics.filter(
    isScreenerMetric
  ) as ScreenerMetricValues[];

  const { page, setPage } = useScreenerPageSync();
  const { sorting, setSorting } = useScreenerSortingSync();

  const prevEffectiveFiltersRef = useRef(effectiveFilters);
  const effectiveFiltersChanged = !isEqual(
    effectiveFilters,
    prevEffectiveFiltersRef.current
  );

  const filtersState = useMemo(() => {
    if (!primaryDimension || screenerMetrics.length === 0) {
      return {
        filtersWithoutPagination: null,
        shouldClearSegmentedSorting: false,
      };
    }

    const firstMetric = screenerMetrics[0] ?? 'screener_headcount';
    const filtersWithPagination = buildScreenerFilters({
      effectiveFilters,
      primaryDimension,
      metric: firstMetric,
      pagination: { limit: LIMIT, offset: 0 },
      sorting,
    });
    const {
      pagination: _pagination,
      sorting: _sorting,
      ...withoutPagination
    } = filtersWithPagination || {};

    const segmentKey = getSegmentKeyFromSort(sorting?.sort_by);
    const segmentEntries = withoutPagination?.segments
      ? Object.entries(withoutPagination.segments)
      : [];
    const segmentValue = segmentKey
      ? segmentEntries.find(([key]) => key === segmentKey)?.[1]
      : undefined;
    const segmentCount = Array.isArray(segmentValue) ? segmentValue.length : 0;
    const shouldClearSegmentedSorting = Boolean(
      (sorting?.num_col ?? 0) > 0 && segmentCount < (sorting?.num_col ?? 0)
    );

    return {
      filtersWithoutPagination: withoutPagination,
      shouldClearSegmentedSorting,
    };
  }, [effectiveFilters, primaryDimension, screenerMetrics, sorting]);

  const { filtersWithoutPagination, shouldClearSegmentedSorting } =
    filtersState;

  const prevFiltersRef = useRef(filtersWithoutPagination);

  const handleSetPage = useCallback(
    (newPage: number) => {
      setPage(newPage);
    },
    [setPage]
  );

  const handleSetSorting = useCallback(
    (newSorting: ScreenerReqSorting | undefined) => {
      setSorting(newSorting);
    },
    [setSorting]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    const filtersEqual = isEqual(
      prevFiltersRef.current,
      filtersWithoutPagination
    );
    const hasValidPageParam = isValidPageParam(pageParam);
    if (!filtersEqual) {
      if (!hasValidPageParam) {
        setPage(1);
      }
    }

    if (shouldClearSegmentedSorting) {
      setSorting(undefined);
    }

    prevFiltersRef.current = filtersWithoutPagination;
    prevEffectiveFiltersRef.current = effectiveFilters;
  }, [
    effectiveFilters,
    filtersWithoutPagination,
    setPage,
    setSorting,
    effectiveFiltersChanged,
    shouldClearSegmentedSorting,
    sorting,
    pageParam,
  ]);

  if (
    !primaryDimension ||
    screenerMetrics.length === 0 ||
    effectiveMetrics.some((m) => !isScreenerMetric(m))
  ) {
    return children({
      data: {
        rows: [],
        totalCount: 0,
        page: 1,
        totalPages: 0,
        setPage,
        sorting: undefined,
        setSorting: handleSetSorting,
      },
      status: DataProviderQueryStatus.Success,
    });
  }

  return (
    <ScreenerTableDataProvider
      primaryDimension={primaryDimension}
      metrics={screenerMetrics}
      pagination={{ offset: (page - 1) * LIMIT, limit: LIMIT }}
      sorting={shouldClearSegmentedSorting ? undefined : sorting}
    >
      {({ data, status }) => {
        const totalPages = Math.ceil((data?.totalCount || 0) / LIMIT);

        return children({
          data: {
            rows: data?.data || [],
            totalCount: data?.totalCount || 0,
            page,
            totalPages,
            setPage: handleSetPage,
            sorting,
            setSorting: handleSetSorting,
          },
          status,
        });
      }}
    </ScreenerTableDataProvider>
  );
};
